import { Card, CardContent, Typography, Box, Divider } from '@mui/material'
import { useTranslator } from '@/hooks'
import { useState } from 'react'

export const FinancialSummary = ({ data }: { data: any }) => {
  const { __, __n } = useTranslator()
  const [showMore, setShowMore] = useState(false)

  const summaryItems = [
    {
      label: `${__('Employee Fee')}`,
      value: data?.employee_fee?.employee_fee,
      icon: 'tabler-file-dollar'
    },
    {
      label: `${__('Voucher')}`,
      value: data?.voucher?.voucher,
      icon: 'tabler-receipt-tax'
    },
    {
      label: `${__('Disbursement Rate')}`,
      value: data?.different?.total_actual_salary_reconcile
        ? Number(
            (
              (Number(data.different.total_ewa_disbursement) / Number(data.different.total_actual_salary_reconcile)) *
              100
            ).toFixed(2)
          )
        : 0,
      icon: 'tabler-scale',
      isNegative: true
    },
    {
      label: `${__('Cumulative Current Debt')}`,
      value: data?.cumulative_current_debt?.cumulative_current_debt,
      icon: 'tabler-presentation-analytics'
    }
  ]

  const servicesDetailItems = [
    {
      label: `${__('Services Fee Tax')} ${Number(data?.employee_fee?.vat_rate ?? 0).toFixed(2)}%`,
      value: data?.employee_fee?.employee_fee_tax,
      icon: 'tabler-cash-banknote'
    },
    {
      label: `${__('Collected')}`,
      value: data?.employee_fee?.employee_fee_collected,
      icon: 'tabler-cash'
    }
  ]

  const voucherDetailItems = [
    {
      label: `${__('Fee Tax')} ${Number(data?.voucher?.vat_rate ?? 0).toFixed(2)}%`,
      value: data?.voucher?.voucher_fee_tax,
      icon: 'tabler-cash-banknote'
    },
    { label: `${__('Collected')}`, value: data?.voucher?.collected, icon: 'tabler-cash' },
    { label: `${__('Debt')}`, value: data?.voucher?.debt, icon: 'tabler-cash-banknote-off' }
  ]

  const differentDetailItems = [
    { label: `${__('Total EWA disbursement')}`, value: data?.different?.total_ewa_disbursement },
    { label: `${__('Total cummulative eligible amount')}`, value: data?.different?.total_actual_salary_reconcile }
  ]

  const debtDetailItems = [
    {
      label: `${data?.cumulative_current_debt?.dept_month_current?.month} ${__('Debt')}`,
      value: data?.cumulative_current_debt?.dept_month_current?.debt
    },
    {
      label: `${data?.cumulative_current_debt?.dept_month_current_month_1?.month} ${__('Debt')}`,
      value: data?.cumulative_current_debt?.dept_month_current_month_1?.debt
    },
    {
      label: `${data?.cumulative_current_debt?.dept_month_current_month_2?.month} ${__('Debt')}`,
      value: data?.cumulative_current_debt?.dept_month_current_month_2?.debt
    }
  ]

  return (
    <Card className='rounded-[16px]'>
      <CardContent className='p-6'>
        <div className='flex flex-col gap-6'>
          <div className='flex justify-between items-start'>
            <div className='flex-1 pr-8'>
              <div className='flex items-center gap-4 mb-3'>
                <Box className='flex items-center justify-between w-full gap-2'>
                  <div className='flex flex-col'>
                    <Typography className='text-[20px] font-semibold'>{__n(summaryItems[0].value)} đ</Typography>
                    <Typography className='text-[15px] font-normal leading-[22px]'>
                      {__(summaryItems[0].label)}
                    </Typography>
                  </div>
                  <Box className='flex items-center p-2 rounded-[6px] bg-gray-100'>
                    <i className={`${summaryItems[0].icon} ]`} />
                  </Box>
                </Box>
              </div>

              {showMore && (
                <div className='space-y-2'>
                  <Divider />
                  {servicesDetailItems.map((item, index) => (
                    <div key={index}>
                      <div className='flex justify-between items-center py-1'>
                        <div className='flex items-center gap-2'>
                          <Typography className='text-[15px] font-medium leading-[22px]'>
                            {__n(item.value, 'đ')}
                          </Typography>
                        </div>
                        <div className='flex flex-row items-center gap-2'>
                          <Typography className='text-[15px] font-normal leading-[22px]'>{__(item.label)}</Typography>
                          <i className={`${item.icon} `} />
                        </div>
                      </div>

                      {index !== 0 && <Divider />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Divider orientation='vertical' flexItem />

            <div className='flex-1 pl-8'>
              <div className='flex items-center gap-4 mb-3'>
                <Box className='flex items-center justify-between w-full gap-2'>
                  <div className='flex flex-col'>
                    <Typography className='text-[20px] font-semibold'>{__n(summaryItems[1].value)} đ</Typography>
                    <Typography className='text-[15px] font-normal leading-[22px]'>
                      {__(summaryItems[1].label)}
                    </Typography>
                  </div>
                  <Box className='flex items-center p-2 rounded-[6px] bg-gray-100'>
                    <i className={`${summaryItems[1].icon} `} />
                  </Box>
                </Box>
              </div>

              {showMore && (
                <div className='space-y-2'>
                  <Divider />
                  {voucherDetailItems.map((item, index) => (
                    <div key={index}>
                      <div className='flex justify-between items-center py-1'>
                        <div className='flex items-center gap-2'>
                          <Typography className='text-[15px] font-medium leading-[22px]'>
                            {__n(item.value, 'đ')}
                          </Typography>
                        </div>
                        <div className='flex flex-row items-center gap-2'>
                          <Typography className='text-[15px] font-normal leading-[22px]'>{__(item.label)}</Typography>
                          <i className={`${item.icon} `} />
                        </div>
                      </div>

                      {index !== 0 && index !== voucherDetailItems.length - 1 && <Divider />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Divider />

          <div className='flex justify-between items-start'>
            <div className='flex-1 pr-8'>
              <div className='flex items-center gap-4 mb-3'>
                <Box className='flex items-center justify-between w-full gap-2'>
                  <div className='flex flex-col'>
                    <Typography
                      className={`text-[20px] font-semibold ${(Number(data?.different?.total_ewa_disbursement) || 0) === (Number(data?.different?.total_actual_salary_reconcile) || 0) ? 'text-green-500' : (Number(data?.different?.total_actual_salary_reconcile) || 0) > (Number(data?.different?.total_ewa_disbursement) || 0) ? 'text-green-500' : 'text-red-500'}`}
                    >
                      {summaryItems[2].value}%
                    </Typography>
                    <Typography className='text-[15px] font-normal leading-[22px]'>
                      {__(summaryItems[2].label)}
                    </Typography>
                  </div>
                  <Box className='flex items-center p-2 rounded-[6px] bg-gray-100'>
                    <i className={`${summaryItems[2].icon} `} />
                  </Box>
                </Box>
              </div>

              {showMore && (
                <div className='space-y-1'>
                  {differentDetailItems.map((item, index) => (
                    <div key={index}>
                      <Divider />
                      <div className='flex justify-between items-center py-1'>
                        <div className='flex items-center gap-2'>
                          <Typography className='text-[15px] font-medium leading-[22px]'>
                            {__n(item.value, 'đ')}
                          </Typography>
                        </div>
                        <div className='flex flex-row items-center gap-2'>
                          <Typography className='text-[15px] font-normal leading-[22px]'>{__(item.label)}</Typography>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Divider orientation='vertical' flexItem />

            <div className='flex-1 pl-8'>
              <div className='flex items-center gap-4 mb-3'>
                <Box className='flex items-center justify-between w-full gap-2'>
                  <div className='flex flex-col'>
                    <Typography className='text-[20px] font-semibold'>{__n(summaryItems[3].value, 'đ')}</Typography>
                    <Typography className='text-[15px] font-normal leading-[22px]'>
                      {__(summaryItems[3].label)}
                    </Typography>
                  </div>
                  <Box className='flex items-center p-2 rounded-[6px] bg-gray-100'>
                    <i className={`${summaryItems[3].icon} `} />
                  </Box>
                </Box>
              </div>

              {showMore && (
                <div className='space-y-1'>
                  {debtDetailItems.map((item, index) => (
                    <div key={index}>
                      <Divider />
                      <div className='flex justify-between items-center py-1'>
                        <>
                          <div className='flex items-center gap-2'>
                            <Typography className='text-[15px] font-medium leading-[22px]'>
                              {__n(item.value, 'đ')}
                            </Typography>
                          </div>
                          <div className='flex flex-row items-center gap-2'>
                            <Typography className='text-[15px] font-normal leading-[22px]'>{__(item.label)}</Typography>
                          </div>
                        </>
                      </div>
                    </div>
                  ))}

                  <Divider />
                  <div className='flex justify-end py-1'>
                    <div
                      className='flex items-center gap-2 cursor-pointer text-gray-500 hover:text-gray-700'
                      onClick={() => setShowMore(false)}
                    >
                      <Typography className='text-[15px] font-normal leading-[22px]'>{__('View Less')}</Typography>
                      <i className='tabler-circle-arrow-up' />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {!showMore && (
            <div className='flex justify-end'>
              <div
                className='flex items-center gap-2 cursor-pointer text-gray-500 hover:text-gray-700'
                onClick={() => setShowMore(true)}
              >
                <Typography className='text-[14px]'>{__('View More')}</Typography>
                <i className='tabler-circle-arrow-down ' />
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
